<template>
    <div>
        <h2 class="mb-1">{{ $t("settings.account.Profile") }}</h2>

        <b-form-group
            id="fieldset-profile-picture"
            :description="$t('settings.account.picture.description')"
            :label="$t('settings.account.picture.label')"
            content-cols-lg="7"
            content-cols-sm
            label-cols-lg="3"
            label-cols-sm="4"
            label-for="name"
        >
            <!-- wrapper span to work around the fact that @click doesn't seem to trigger on the b-avatar element -->
            <span @click="openFileSelector">
                <b-avatar
                    id="profile-picture"
                    :class="{ initials: !userData.pictureUrl }"
                    :src="userData.pictureUrl"
                    :text="resolveInitials(userData.name)"
                    class="round-large cursor-pointer"
                    size="64"
                />
            </span>

            <b-form-file v-show="false" ref="imageInput" accept="image/*" @input="setProfilePicture" />
            <span v-if="assetIsUploading" class="ml-1">
                <b-spinner small />{{ $t("settings.account.picture.uploading") }}
            </span>
            <b-button v-else-if="userData.pictureUrl" class="ml-1" variant="primary" @click="userData.pictureUrl = null"
                >{{ $t("settings.account.picture.remove") }}
            </b-button>
        </b-form-group>

        <validation-provider #default="{ errors }" name="name" rules="required">
            <b-form-group
                id="fieldset-name"
                :description="$t('settings.account.fullName.description')"
                :label="$t('settings.account.fullName.label')"
                :state="errors.length > 0 ? false : null"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="name"
            >
                <b-form-input id="name" v-model="userData.name" disabled type="text" />
            </b-form-group>

            <b-form-group
                id="fieldset-displayName"
                :description="$t('settings.account.displayName.description')"
                :label="$t('settings.account.displayName.label')"
                :state="errors.length > 0 ? false : null"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="name"
            >
                <b-form-input
                    id="displayName"
                    v-model="userData.displayName"
                    :state="errors.length > 0 ? false : null"
                    type="text"
                />
                <small class="text-danger">{{ errors[0] }}</small>
            </b-form-group>

            <b-form-group
                id="fieldset-email"
                :description="$t('settings.account.email.description')"
                :label="$t('settings.account.email.label')"
                :state="errors.length > 0 ? false : null"
                content-cols-lg="7"
                content-cols-sm
                label-cols-lg="3"
                label-cols-sm="4"
                label-for="name"
            >
                <b-form-input id="email-address" v-model="userData.email" disabled type="text" />
            </b-form-group>
        </validation-provider>

        <hr />
    </div>
</template>

<script>
import useFileUploads from "@/composables/useFileUploads";
import useImageConverters from "@/composables/useImageConverters";
import ToastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import i18n from "@/libs/i18n";
import { getFullPath } from "@/utils/assets";
import { resolveInitials } from "@/utils/users";
import { required } from "@validations";
import { ref } from "@vue/composition-api";
import {
    BAvatar,
    BButton,
    BCard,
    BContainer,
    BFormCheckbox,
    BFormFile,
    BFormGroup,
    BFormInput,
    BSpinner,
} from "bootstrap-vue";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import { useToast } from "vue-toastification/composition";

export default {
    components: {
        BContainer,
        BFormGroup,
        BFormInput,
        BFormCheckbox,
        BCard,
        BAvatar,
        BFormFile,
        BButton,
        BSpinner,
        ValidationProvider,
        ValidationObserver,
    },
    props: {
        userData: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            required,
        };
    },
    setup(props) {
        const toast = useToast();
        const { uploadFile } = useFileUploads();
        const { cropToSquare } = useImageConverters();
        const assetIsUploading = ref(false);
        const imageInput = ref(null);

        const setProfilePicture = async (file) => {
            assetIsUploading.value = true;
            const fileFormat = file.type.split("/").pop();

            let fileToUpload = file;
            if (fileFormat !== "gif") {
                fileToUpload = await cropToSquare(file, 512).catch(() => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.account.picture.uploaded.failure"),
                            text: i18n.t("settings.account.picture.uploaded.failure-text"),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                });
            }

            if (!fileToUpload) {
                assetIsUploading.value = false;
                return;
            }

            uploadFile(fileToUpload, 0.5, `users/${props.userData.id}/profile/`)
                .then((uploadedFile) => {
                    props.userData.pictureUrl = getFullPath(uploadedFile);
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.account.picture.uploaded.success"),
                            text: i18n.t("settings.account.picture.uploaded.success-text"),
                            icon: "check",
                            variant: "success",
                        },
                    });
                })
                .catch((error) => {
                    toast({
                        component: ToastificationContent,
                        props: {
                            title: i18n.t("settings.account.picture.uploaded.failure"),
                            text:
                                typeof error === "string"
                                    ? error
                                    : i18n.t("settings.account.picture.uploaded.failure-text"),
                            icon: "times",
                            variant: "danger",
                        },
                    });
                })
                .finally(() => (assetIsUploading.value = false));
        };

        const openFileSelector = () => {
            imageInput.value.$el.children[0].click();
        };

        return {
            assetIsUploading,
            imageInput,
            setProfilePicture,
            resolveInitials,
            openFileSelector,
        };
    },
};
</script>
<style lang="scss" scoped>
@import "@/assets/scss/variables/variables";

.b-avatar.initials {
    background-color: $alt-avatar-background-color;
    color: white;
}
</style>
